import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import AuthLayout from "./AuthLayout";
import { adminApi, formatApiError } from "../../lib/authClient";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await adminApi.post("/auth/forgot-password", { email });
      setSent(true);
      if (data.emailed) {
        toast.success("Reset link sent to your email.");
      } else {
        toast.success("Request received. Check your email for the reset link.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <AuthLayout title="Check your inbox" data-testid="forgot-sent">
        <div className="text-center" data-testid="forgot-confirmation">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-white/15">
            <CheckCircle2 size={34} className="text-white" />
          </div>
          <p className="mt-6 text-white/70 leading-relaxed">
            If an account exists for <span className="text-white">{email}</span>, we’ve sent a
            secure link to reset your password. The link expires in 1 hour.
          </p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Forgot Password"
      subtitle="Enter your admin email and we'll send you a reset link."
    >
      <form onSubmit={submit} data-testid="forgot-form" className="space-y-4">
        <div className="relative">
          <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/65" />
          <input
            data-testid="forgot-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Admin email address"
            className="input-dark pl-11"
          />
        </div>
        <motion.button
          type="submit"
          disabled={loading}
          whileTap={{ scale: 0.97 }}
          data-testid="forgot-submit"
          className="flex w-full items-center justify-center gap-2 rounded-2xl bg-white px-6 py-4 font-semibold text-black transition-colors hover:bg-white/90 disabled:opacity-60"
        >
          {loading ? <Loader2 size={18} className="animate-spin" /> : "Send Reset Link"}
        </motion.button>
      </form>
    </AuthLayout>
  );
}
